// src/features/quotation/types/quotationTypes.js
/**
 * ============================================
 * QUOTATION MODULE - TYPES & CONSTANTS
 * ============================================
 * This file contains all type definitions, enums,
 * and constants used throughout the quotation module.
 *
 * @version 1.0.0
 * @author Hashtag Photography Team
 * @lastUpdated 2024-01-24
 * ============================================
 */

/**
 * Quotation Status Enum
 * Defines all possible states a quotation can be in
 */
export const QUOTATION_STATUS = {
  DRAFT: "draft",
  SENT: "sent",
  VIEWED: "viewed",
  APPROVED: "approved",
  REJECTED: "rejected",
  EXPIRED: "expired",
  CONVERTED: "converted",
};

/**
 * Quotation Status Labels
 * Human-readable labels for each status
 */
export const QUOTATION_STATUS_LABELS = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  converted: "Converted",
};

/**
 * Quotation Status Colors
 * Color codes for each status (used for badges, chips, etc.)
 */
export const QUOTATION_STATUS_COLORS = {
  draft: "#d9d9d9",
  sent: "#1890ff",
  viewed: "#13c2c2",
  approved: "#52c41a",
  rejected: "#ff4d4f",
  expired: "#faad14",
  converted: "#722ed1",
};

/**
 * Quotation Status Icons
 * Icon names for each status
 */
export const QUOTATION_STATUS_ICONS = {
  draft: "📄",
  sent: "📤",
  viewed: "👁️",
  approved: "✅",
  rejected: "❌",
  expired: "⏰",
  converted: "🔄",
};

/**
 * Quotation Status Transitions
 * Defines which status transitions are allowed
 */
export const QUOTATION_STATUS_TRANSITIONS = {
  draft: ["sent", "expired"],
  sent: ["viewed", "expired"],
  viewed: ["approved", "rejected", "expired"],
  approved: ["converted"],
  rejected: [],
  expired: [],
  converted: [],
};

/**
 * Quotation Discount Types
 */
export const DISCOUNT_TYPES = {
  PERCENTAGE: "percentage",
  FIXED: "fixed",
};

/**
 * Quotation Currency Options
 * Supported currencies
 */
export const CURRENCY_OPTIONS = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
];

/**
 * Quotation Filter Options
 */
export const FILTER_OPTIONS = [
  { key: "status", label: "Status", type: "select" },
  { key: "dateRange", label: "Date Range", type: "dateRange" },
  { key: "clientId", label: "Client", type: "select" },
  { key: "minAmount", label: "Min Amount", type: "number" },
  { key: "maxAmount", label: "Max Amount", type: "number" },
  { key: "tags", label: "Tags", type: "tags" },
  { key: "search", label: "Search", type: "text" },
];

/**
 * Quotation Sort Options
 */
export const SORT_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "total", label: "Total Amount" },
  { value: "clientName", label: "Client Name" },
  { value: "status", label: "Status" },
  { value: "createdAt", label: "Created Date" },
  { value: "validUntil", label: "Expiry Date" },
];

/**
 * Quotation Default Values
 */
export const DEFAULT_VALUES = {
  status: QUOTATION_STATUS.DRAFT,
  currency: "USD",
  taxRate: 10,
  discountType: DISCOUNT_TYPES.PERCENTAGE,
  showTax: true,
  showDiscount: true,
  showNotes: true,
  showTerms: true,
  items: [
    {
      id: "item-1",
      description: "",
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      notes: "",
    },
  ],
};

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  FETCH_FAILED: "Failed to fetch quotations. Please try again.",
  CREATE_FAILED: "Failed to create quotation. Please check your inputs.",
  UPDATE_FAILED: "Failed to update quotation. Please try again.",
  DELETE_FAILED: "Failed to delete quotation. Please try again.",
  CONVERT_FAILED: "Failed to convert quotation to invoice. Please try again.",
  SEND_FAILED: "Failed to send quotation. Please try again.",
  STATUS_UPDATE_FAILED: "Failed to update quotation status. Please try again.",
  INVALID_STATUS_TRANSITION:
    "Invalid status transition. Please check the status flow.",
  ITEMS_REQUIRED: "At least one item is required.",
  CLIENT_REQUIRED: "Client information is required.",
  VALID_UNTIL_PAST: "Valid until date must be in the future.",
  PERMISSION_DENIED: "You do not have permission to perform this action.",
};

/**
 * Success Messages
 */
export const SUCCESS_MESSAGES = {
  CREATE_SUCCESS: "Quotation created successfully!",
  UPDATE_SUCCESS: "Quotation updated successfully!",
  DELETE_SUCCESS: "Quotation deleted successfully!",
  CONVERT_SUCCESS: "Quotation converted to invoice successfully!",
  SEND_SUCCESS: "Quotation sent successfully!",
  STATUS_UPDATE_SUCCESS: "Quotation status updated successfully!",
};
